import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import produce from 'immer';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import AddressDropdown from './AddressDropdown';
import Contact from './Contact';
import { useFormContext } from './FormContext';
import { validateFacilityInfo } from './ValidateFacilityInfo';
import { ContactType, Facility, deliveryOptions } from './types';
import DeliveryOptionsDropdown from './DeliveryOptionsDropdown';

interface Props {
    showForm: boolean;
    onClose: () => void;
    setFacilityInfoForm: React.Dispatch<React.SetStateAction<boolean>>;
    facilityToEdit: Facility | null;
    facilityIndex: number | null;
    setFacilityIndex: React.Dispatch<React.SetStateAction<number | null>>;
}

const baseFacilityInfo: Facility = {
    businessInfo: {
        facilityName: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        businessType: { id: 0, name: '' },
        isCorporate: false
    },
    shippingInfo: {
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        sameAsBusiness: false,
        deliveryOption: { id: 0, name: '' },
        contact: {} as ContactType,
    },
    billingInfo: {
        sameAsCorporate: true,
        sameAsShipping: false,
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        contact: {} as ContactType,
    },
    contactInfo: {
        contacts: [],
    },
    parentId: null,
};

const FacilityInfo = (props: Props) => {
    const accordionOrder = ['facility-info', 'contact-info', 'billing-info'];
    const [expandedSections, setExpandedSections] = useState<string[]>(props.facilityToEdit ? accordionOrder : [
        'facility-info',
    ]);
    const [facilityInfo, setFacilityInfo] = useState<Facility>(baseFacilityInfo);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const { setFormData, formData } = useFormContext();

    useEffect(() => {
        if (props.facilityToEdit && props.facilityIndex !== null) {
            setFacilityInfo(props.facilityToEdit);
        } else {
            setFacilityInfo(baseFacilityInfo);
        }
    }, [props.showForm, props.facilityToEdit, props.facilityIndex]);

    const handleAccordionChange =
        (panel: string) => (event: any, isExpanded: boolean) => {
            setExpandedSections((prev) =>
                isExpanded
                    ? [...prev, panel]
                    : prev.filter((section) => section !== panel)
            );
        };

    const validateSection = (
        section: 'facility-info' | 'contact-info' | 'billing-info'
    ) => {
        const { isValid, errors: sectionErrors } = validateFacilityInfo(
            facilityInfo,
            formData,
            section
        );

        if (!isValid) {
            setErrors(sectionErrors);
        } else {
            setErrors({});
        }

        return isValid;
    };

    const goToNextSection = (
        currentSection: 'facility-info' | 'contact-info' | 'billing-info'
    ) => {
        const isValid = validateSection(currentSection);
        if (!isValid) return;

        const currentIndex = accordionOrder.indexOf(currentSection);
        const nextSection = accordionOrder[currentIndex + 1];
        if (nextSection) setExpandedSections([nextSection]);
    };

    const changeFacilityInfo = (
        section: keyof Facility,
        field: string,
        value: any
    ) => {
        setFacilityInfo((prev) => ({
            ...prev,
            [section]: {
                ...(prev[section] as object),
                [field]: value,
            },
        }));
    };

    const saveFacility = () => {
        const { isValid, errors } = validateFacilityInfo(facilityInfo, formData);

        if (!isValid) {
            setErrors(errors);
            return;
        }

        setFormData((prevFormData) => {
            const updatedFacilities = [...prevFormData.facilities];
            if (props.facilityIndex !== null) {
                updatedFacilities[props.facilityIndex] = facilityInfo;
            } else {
                updatedFacilities.push(facilityInfo);
            }
            return { ...prevFormData, facilities: updatedFacilities };
        });

        props.setFacilityInfoForm(false);
        props.setFacilityIndex(null);
    };

    const changeContactField = (
        section: 'shippingInfo' | 'billingInfo',
        field: keyof ContactType,
        value: any
    ) => {
        setFacilityInfo((prev) =>
            produce(prev, (draft: any) => {
                draft[section].contact[field] = value;

                if (section === 'shippingInfo') {
                    draft[section].contact.contactType = 'Shipping';
                } else if (section === 'billingInfo') {
                    draft[section].contact.contactType = 'Billing';
                }
            })
        );
    };

    return (
        <Box
            sx={{
                position: 'absolute',
                right: 0,
                top: 65,
                minHeight: '100vh',
                backgroundColor: '#fff',
                boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                padding: '20px',
                transition: 'transform 0.3s ease',
                transform: props.showForm ? 'translateX(0)' : 'translateX(100%)',
                zIndex: 100,
                width: '50%',
                paddingLeft: 8,
            }}
        >
            <Typography
                style={{
                    color: '#808080',
                    fontSize: 28,
                    marginBottom: 24,
                    marginTop: 70,
                }}
            >
                {props.showForm && props.facilityToEdit ? 'Edit Facility' : 'Add Child Facility'}
            </Typography>

            <Stack>
                {/* Facility Information Accordion */}
                <Accordion
                    expanded={expandedSections.includes('facility-info')}
                    onChange={handleAccordionChange('facility-info')}
                    sx={{ marginBottom: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Facility Information</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TextField
                            label='Facility Name'
                            value={facilityInfo.businessInfo.facilityName}
                            onChange={(e) =>
                                changeFacilityInfo('businessInfo', 'facilityName', e.target.value)
                            }
                            fullWidth
                            required
                            margin='normal'
                            sx={{ backgroundColor: '#fff' }}
                            error={!!errors.facilityName}
                        />
                        <AddressDropdown
                            setAddress1={(value) =>
                                changeFacilityInfo('businessInfo', 'address', value)
                            }
                            setFullAddress={(addr) => {
                                changeFacilityInfo('businessInfo', 'address', addr.address);
                                changeFacilityInfo('businessInfo', 'address2', addr.address2);
                                changeFacilityInfo('businessInfo', 'city', addr.city);
                                changeFacilityInfo('businessInfo', 'state', addr.state);
                                changeFacilityInfo('businessInfo', 'zip', addr.zip);
                            }}
                            inputValue={facilityInfo.businessInfo.address}
                            data={facilityInfo.businessInfo}
                            errorKey='facilityAddress'
                            errors={errors}
                            onChange={(field, value) =>
                                changeFacilityInfo('businessInfo', field, value)
                            }
                            errorCityKey='facilityCity'
                            errorStateKey='facilityState'
                            errorZipKey='facilityZip'
                        />
                        <DeliveryOptionsDropdown
                            errors={errors}
                            onChangeChild={changeFacilityInfo}
                            data={facilityInfo.shippingInfo}
                            isChild={true}
                        />
                        <Stack justifyContent='flex-end' direction='row'>
                            <Button
                                variant='contained'
                                color='primary'
                                sx={{ mt: 3 }}
                                onClick={() => goToNextSection('facility-info')}
                            >
                                Continue
                            </Button>
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* Shipping Contact Accordion */}
                <Accordion
                    expanded={expandedSections.includes('contact-info')}
                    onChange={handleAccordionChange('contact-info')}
                    sx={{ marginBottom: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Shipping Contact</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Contact
                            contact={facilityInfo.shippingInfo.contact}
                            handleChange={(field, value) =>
                                changeContactField('shippingInfo', field, value)
                            }
                            isTitle
                            errors={errors}
                            errorKey='shippingContact'
                        />
                        <Stack justifyContent='flex-end' direction='row'>
                            <Button
                                variant='contained'
                                color='primary'
                                sx={{ mt: 3 }}
                                onClick={() => goToNextSection('contact-info')}
                            >
                                Continue
                            </Button>
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* Billing Information Accordion */}
                <Accordion
                    expanded={expandedSections.includes('billing-info')}
                    onChange={handleAccordionChange('billing-info')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Billing Information</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant='body1' marginTop={3}>
                            Billing same as corporate?
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={facilityInfo.billingInfo.sameAsCorporate}
                                        onChange={(e) =>
                                            changeFacilityInfo(
                                                'billingInfo',
                                                'sameAsCorporate',
                                                e.target.checked
                                            )
                                        }
                                        color='info'
                                    />
                                }
                                label='Yes'
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!facilityInfo.billingInfo.sameAsCorporate}
                                        onChange={(e) =>
                                            changeFacilityInfo(
                                                'billingInfo',
                                                'sameAsCorporate',
                                                !e.target.checked
                                            )
                                        }
                                        color='info'
                                    />
                                }
                                label='No'
                            />
                        </FormGroup>

                        {!facilityInfo.billingInfo.sameAsCorporate && (
                            <AddressDropdown
                                setAddress1={(value) =>
                                    changeFacilityInfo('billingInfo', 'address', value)
                                }
                                setFullAddress={(addr) => {
                                    changeFacilityInfo('billingInfo', 'address', addr.address);
                                    changeFacilityInfo('billingInfo', 'address2', addr.address2);
                                    changeFacilityInfo('billingInfo', 'city', addr.city);
                                    changeFacilityInfo('billingInfo', 'state', addr.state);
                                    changeFacilityInfo('billingInfo', 'zip', addr.zip);
                                }}
                                inputValue={facilityInfo.billingInfo.address}
                                data={facilityInfo.billingInfo}
                                errorKey='facilityBillingAddress'
                                errors={errors}
                                onChange={(field, value) =>
                                    changeFacilityInfo('billingInfo', field, value)
                                }
                                errorCityKey='facilityBillingCity'
                                errorStateKey='facilityBillingState'
                                errorZipKey='facilityBillingZip'
                            />
                        )}

                        <Typography style={{ color: '#808080', fontSize: 18, marginTop: 16 }}>
                            Contact Information
                        </Typography>

                        <Contact
                            contact={facilityInfo.billingInfo.contact}
                            handleChange={(field, value) =>
                                changeContactField('billingInfo', field, value)
                            }
                            errors={errors}
                            errorKey='billingContact'
                        />
                        <Stack justifyContent='flex-end' direction='row'>
                            <Button
                                variant='contained'
                                color='primary'
                                sx={{ mt: 3 }}
                                onClick={saveFacility}
                            >
                                Save Facility
                            </Button>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            </Stack>
        </Box>
    );
};

export default FacilityInfo;
